<template>
    <div class="page page-layout requisition">
        <div v-if="!getToggleMapState" class="flexbox-fullheight form-view page-container">
            <header class="page-header">
                <div class="page-header-background"></div>

                <template v-if="!loading">
                    <router-link class="return-to-dashboard" :to="{ name: 'dashboard', hash: '#requisitions' }">
                        <icon class="arrow-left-circle-svg large-svg fill-white stroke-white" name="arrow-left" />
                    </router-link>

                    <div class="grid-x">
                        <div class="cell large-8">
                            <nav class="page-breadcrumbs">
                                <ol class="page-breadcrumbs-list" role=list>
                                    <li class="page-breadcrumbs-item"><router-link class="requisition-navigation-item" :to="{ name: 'dashboard', hash: '#requisitions' }">Oversigt</router-link></li>
                                    <li class="page-breadcrumbs-item" aria-current="page">
                                        <icon class="small-svg fill-white" name="chevron-right"></icon>
                                        <icon class="small-svg fill-white" name="requisition"></icon>
                                        Entreprenøropgave
                                    </li>
                                </ol>
                            </nav>

                            <h1 class="page-heading">
                                <div v-if="report.requisition && report.requisition.serialNumber" style="font-size: 0.8rem; font-style: italic;">Løbenummer: {{ report.requisition.serialNumber }}</div>
                                {{ report.name }}
                            </h1>

                            <div class="flexbox align-items flexbox-wrap" style="gap: 6px 18px;">
                                <div class="flexbox-center page-header-info">
                                    <icon class="medium-svg fill-white" name="user-reverse-helmet" />
                                    <select v-model="report.ownerUserId" @change="selectEntrepreneur($event)" class="select-options">
                                        <option v-for="(responsible, index) in organizationUserList" :key="index" :value="responsible.userId" :selected="responsible.userId">
                                            <icon class="user small-svg fill-white" name="user" />
                                            {{ responsible.name }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div class="cell large-2 page-header-actions">
                            <template v-if="report.requisition && report.requisition.status === 'Ny' && requisitionStatus === null">
                                <button class="button button-big active-button" @click="accept()">Accepter</button>
                            </template>
                            <template v-else>
                                <div style="position: relative;">
                                    <button class="button button-big active-button" :disabled="checkApprove ? 'true' : null" v-if="!hideApproveButton && report.requisition && !report.requisition.report.report.readyForApproval" @click="readyForapproval()">{{ approveStatus }}</button>
                                    <div id="disabled-decription" class="disabled-description" role="tooltip" v-if="!hideApproveButton && checkApprove">
                                        Du kan først færdigmelde,<wbr /> når alle aktiviteter er udført.<br />
                                        <em>Kun {{ reportedActivities }} af {{ totalActivityCount }} aktiviteter er udført.</em>
                                        <!-- optælling skal også justeres -->
                                        <div class="disabled-description-arrow"></div>
                                    </div>
                                </div>
                            </template>
                        </div>
                        <div class="cell large-2 page-header-actions">
                            <p v-if="hideApproveButton">Færdigmeldt: {{ new Date() | formatTime }}</p>
                            <div v-if="!hideApproveButton && report.requisition && report.requisition.report.report.readyForApproval" class="report-done">
                                <h3>Færdigmeldt</h3>
                                <icon class="small-svg fill-white" name="checkmark" />
                            </div>
                        </div>
                    </div>
                </template>
            </header>

            <main class="page-content" v-show="!loading">
                <guide-nav :icons="iconObj" :initStep="step" @changeStep="changedStep" />
                <bottom-nav :icons="iconObj" :initStep="step" @changeStep="changedStep" />

                <!-- ------------- Aktiviteter ------------- -->
                <section v-if="step === 0">
                    <!-- Aktiviteter -->
                    <span class="mobile-map show-for-small-only" @click="toggleMap()">
                        <icon class="small-svg" name="maplocation" />
                        <span>Kort</span>
                    </span>

                    <objects-list :fullAddress="fullAddress" :requisition="report.requisition" :requisitionStatus="requisitionStatus" />

                    <hr />

                    <div v-if="this.requisition.reportObjectSummaries && this.requisition.reportObjectSummaries.length > 0 && showSummaryTable(this.requisition.reportObjectSummaries)">
                        <table v-for="activity in objectSummarySorted" :key="activity.activityId" class="report-object-summary">
                            <thead>
                                <th>{{ activity.activityType }}</th>
                                <th style="text-align: right">Ikke udført</th>
                                <th style="text-align: right">Udført</th>
                                <th style="text-align: right">Samlet</th>
                            </thead>
                            <tbody>
                                <tr v-if="activity.numberOfPointObjectsNotDone + activity.numberOfPointObjectsDone > 0">
                                    <td>Punkter <em>(antal)</em></td>
                                    <td style="text-align: right">{{ activity.numberOfPointObjectsNotDone }}&nbsp;<span class="unit">stk.</span></td>
                                    <td style="text-align: right">{{ activity.numberOfPointObjectsDone }}&nbsp;<span class="unit">stk.</span></td>
                                    <td style="text-align: right"><u>{{ Number.parseFloat((activity.numberOfPointObjectsNotDone + activity.numberOfPointObjectsDone).toFixed(0)) }}&nbsp;<span class="unit">stk.</span></u></td>
                                </tr>
                                <tr v-if="activity.totalLengthNotDone + activity.totalLengthDone > 0">
                                    <td>Linjer <em>(længde)</em></td>
                                    <td style="text-align: right">{{ activity.totalLengthNotDone }}&nbsp;<span class="unit">m</span></td>
                                    <td style="text-align: right">{{ activity.totalLengthDone }}&nbsp;<span class="unit">m</span></td>
                                    <td style="text-align: right"><u>{{ Number.parseFloat((activity.totalLengthNotDone + activity.totalLengthDone).toFixed(2)) }}&nbsp;<span class="unit">m</span></u></td>
                                </tr>
                                <tr v-if="activity.totalAreaNotDone + activity.totalAreaDone > 0">
                                    <td>Flader <em>(areal)</em></td>
                                    <td style="text-align: right">{{ activity.totalAreaNotDone }}&nbsp;<span class="unit">m<sup>2</sup></span></td>
                                    <td style="text-align: right">{{ activity.totalAreaDone }}&nbsp;<span class="unit">m<sup>2</sup></span></td>
                                    <td style="text-align: right"><u>{{ Number.parseFloat((activity.totalAreaNotDone + activity.totalAreaDone).toFixed(2)) }}&nbsp;<span class="unit">m<sup>2</sup></span></u></td>
                                </tr>
                                <tr v-if="activity.numberOfOthersNotDone + activity.numberOfOthersDone > 0">
                                    <td>Øvrige <em>(antal)</em></td>
                                    <td style="text-align: right">{{ activity.numberOfOthersNotDone }}&nbsp;<span class="unit">stk.</span></td>
                                    <td style="text-align: right">{{ activity.numberOfOthersDone }}&nbsp;<span class="unit">stk.</span></td>
                                    <td style="text-align: right"><u>{{ Number.parseFloat((activity.numberOfOthersNotDone + activity.numberOfOthersDone).toFixed(0)) }}&nbsp;<span class="unit">stk.</span></u></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <!-- ------------- Dokumentation ------------- -->
                <section v-if="step === 1" class="mobile-padding">
                    <h2 class="dfiles__heading">Dokumentation</h2>

                    <template v-if="documentationDocumentsSorted.length > 0">
                        <div class="document__options">
                            <div class="document__option">
                                <input type="checkbox" id="file-metadata" :checked="requisitionDocumentsMetadataVisible" @change="documentListMetadataToggle" />
                                <label for="file-metadata">Vis filoplysninger</label>
                            </div>
                        </div>

                        <ul class="document__list">
                            <li class="document__item" v-for="file in documentationDocumentsSorted" :key="file.id">
                                <div class="document__image">
                                    <div class="document__loader"></div>
                                    <img v-if="file.contentType.startsWith('image')" :src="file.dataSource" alt="" loading="lazy" />
                                    <icon v-else :name="getIcon(file)" :class="getIcon(file)" />
                                    <button v-if="file.contentType.startsWith('image')" @click="documentOpen(file)"><span class="visually-hidden">Vis billede</span></button>
                                    <button v-else @click="documentDownload(file)"><span class="visually-hidden">Download fil</span></button>
                                    <div class="document__icon" v-if="file.description && !requisitionDocumentsMetadataVisible" :title="'Kommentar: ' + file.description">
                                        <icon name="info-2" />
                                    </div>
                                </div>
                                <div class="document__info" v-if="requisitionDocumentsMetadataVisible">
                                    <div class="document__name" :title="file.contentType.startsWith('image') ? $filters.formatDateTime(file.photoTaken) : file.name">
                                        <template v-if="file.contentType.startsWith('image')">{{ file.photoTaken | formatDateTime }}</template>
                                        <template v-else>{{ file.name }}</template>
                                    </div>
                                    <div class="document__date"><strong>Upload:</strong> <time :datetime="file.dateCreated">{{ file.dateCreated | formatDateTimeShort }}</time></div>
                                    <div class="document__description" v-if="file.description"><strong>Kommentar:</strong> <span class="truncate" :title="file.description">{{ file.description }}</span></div>
                                </div>
                                <div class="document__actions">
                                    <button title="Download fil" @click="documentDownload(file)">
                                        <span class="visually-hidden">Download</span>
                                        <icon name="download-arrow" />
                                    </button>
                                </div>
                            </li>
                        </ul>
                    </template>
                </section>

                <!-- ------------- Billeder ------------- -->
                <section v-if="step === 2" class="mobile-padding">
                    <n-files :class="{'disabled': report.requisition && report.requisition.status.toLowerCase() === 'ny' && requisitionStatus === null}" :allowedFileTypes="applicationSettings.allowedFileTypes.join(',')" :showList="false" :uploadFunction="uploadRequisitionFiles" :uploadStatus="uploadStatus" :uploadErrorResponse="uploadErrorResponse">
                        <template v-slot:heading>
                            Dokumenter
                        </template>
                        <template v-slot:description>
                            Upload dine billeder og dokumenter.<br />
                            <em>F.eks. *.pdf, *.jpg, *.doc, *.xls</em>
                        </template>

                        <template v-slot:upload-label>
                            <icon class="large-svg" name="upload" /><br />
                            <strong>Klik for at vælge dine filer</strong><br />
                            <small>... eller træk og slip dem her.</small>
                        </template>
                        <template v-slot:upload-title>
                            Klik for at vælge dine filer eller træk og slip dem her.
                        </template>
                        <template v-slot:upload-saving-singluar>
                            Uploader fil...
                        </template>
                        <template v-slot:upload-saving-plural>
                            Uploader flere filer...
                        </template>
                        <template v-slot:upload-success-singluar>
                            Fil uploadet.
                        </template>
                        <template v-slot:upload-success-plural>
                            Filer uploadet.
                        </template>
                        <template v-slot:upload-failed>
                            Upload fejlede:
                        </template>
                    </n-files>

                    <p class="dfiles-list__empty" v-if="requisitionDocuments.length === 0" :class="{'disabled': report.requisition && report.requisition.status.toLowerCase() === 'ny' && requisitionStatus === null}">Der er ikke tilføjet nogle filer endnu.</p>

                    <template v-if="requisitionDocumentsSorted.length > 0">
                        <div class="document__options" :class="{'disabled': report.requisition && report.requisition.status.toLowerCase() === 'ny' && requisitionStatus === null}">
                            <div class="document__option">
                                <input type="checkbox" id="file-metadata" :checked="requisitionDocumentsMetadataVisible" @change="documentListMetadataToggle" />
                                <label for="file-metadata">Vis filoplysninger</label>
                            </div>
                        </div>

                        <ul class="document__list" :class="{'disabled': report.requisition && report.requisition.status.toLowerCase() === 'ny' && requisitionStatus === null}">
                            <li class="document__item" v-for="file in requisitionDocumentsSorted" :key="file.id">
                                <div class="document__image">
                                    <div class="document__loader"></div>
                                    <img v-if="file.contentType.startsWith('image')" :src="file.dataSource" alt="" loading="lazy" />
                                    <icon v-else :name="getIcon(file)" :class="getIcon(file)" />
                                    <button v-if="file.contentType.startsWith('image')" @click="documentOpen(file)"><span class="visually-hidden">Vis billede</span></button>
                                    <button v-else @click="documentDownload(file)"><span class="visually-hidden">Download fil</span></button>
                                    <div class="document__icon" v-if="file.description && !requisitionDocumentsMetadataVisible" :title="'Kommentar: ' + file.description">
                                        <icon name="info-2" />
                                    </div>
                                </div>
                                <div class="document__info" v-if="requisitionDocumentsMetadataVisible">
                                    <div class="document__name" :title="file.contentType.startsWith('image') ? $filters.formatDateTime(file.photoTaken) : file.name">
                                        <template v-if="file.contentType.startsWith('image')">{{ file.photoTaken | formatDateTime }}</template>
                                        <template v-else>{{ file.name }}</template>
                                    </div>
                                    <div class="document__date"><strong>Upload:</strong> <time :datetime="file.dateCreated">{{ file.dateCreated | formatDateTimeShort }}</time></div>
                                    <div class="document__description" v-if="file.description"><strong>Kommentar:</strong> <span class="truncate" :title="file.description">{{ file.description }}</span></div>
                                </div>
                                <div class="document__actions">
                                    <button title="Tilføj kommentar" v-if="checkDescriptionAvailability(file)" @click="commentFile(file)">
                                        <span class="visually-hidden">Tilføj kommentar</span>
                                        <icon name="pencil" />
                                    </button>
                                    <button title="Download fil" @click="documentDownload(file)">
                                        <span class="visually-hidden">Download</span>
                                        <icon name="download-arrow" />
                                    </button>
                                    <button class="delete" title="Slet fil" @click="documentRemove(file.id)">
                                        <span class="visually-hidden">Slet</span>
                                        <icon name="trash" />
                                    </button>
                                </div>
                            </li>
                        </ul>
                    </template>
                </section>

                <!-- ------------- Informationer ------------- -->
                <section v-if="step === 3" class="mobile-padding">
                    <h2 class="no-margin">Informationer</h2>
                    <template v-if="requisition.requestList && requisition.requestList.length > 0">
                        <div class="contact-card">
                            <div class="contact-card__header"><icon name="enquiry" class="small-svg fill-selected-dark-gray" />Anmelder<template v-if="requisition.requestList.length > 1">e</template></div>

                            <div class="contact-card__list">
                                <template v-for="(client, index) in requisition.requestList">
                                    <hr v-if="index > 0" />
                                    <div class="contact-card__item" :key="client.name">
                                        <dl v-if="client.description" class="contact-card__description">
                                            <dt class="contact-card__label">Bemærkning</dt>
                                            <dd class="contact-card__value">{{ client.description }}</dd>
                                        </dl>
                                        <dl v-if="client.problemType" class="contact-card__problem-type">
                                            <dt class="contact-card__label">Problemtype</dt>
                                            <dd class="contact-card__value">{{ client.problemType }}</dd>
                                        </dl>
                                        <dl class="contact-card__name">
                                            <dt class="contact-card__label">Navn</dt>
                                            <dd class="contact-card__value"><template v-if="client.clientName">{{ client.clientName }}</template><template v-else><em>Anonym</em></template></dd>
                                        </dl>
                                        <dl v-if="client.clientPhone" class="contact-card__phone">
                                            <dt class="contact-card__label">Telefon</dt>
                                            <dd class="contact-card__value"><a :href="`tel:${client.clientPhone}`">{{ client.clientPhone }}</a></dd>
                                        </dl>
                                        <dl v-if="client.clientEmail" class="contact-card__email">
                                            <dt class="contact-card__label">E-mail</dt>
                                            <dd class="contact-card__value"><a :href="`mailto:${client.clientEmail}`">{{ client.clientEmail }}</a></dd>
                                        </dl>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </template>
                    <template v-if="report.caseworkerName">
                        <div class="contact-card">
                            <div class="contact-card__header"><icon name="user" class="small-svg fill-selected-dark-gray" /> Sagsbehandler</div>
                            <dl class="contact-card__name">
                                <dt class="contact-card__label">Navn</dt>
                                <dd class="contact-card__value">{{ report.caseworkerName }} <template v-if="!report.caseworkerActive">(INAKTIV)</template></dd>
                            </dl>
                            <dl v-if="report.caseworkerActive && report.caseworkerPhone" class="contact-card__phone">
                                <dt class="contact-card__label">Telefon</dt>
                                <dd class="contact-card__value"><a :href="`tel:${report.caseworkerPhone}`">{{ report.caseworkerPhone }}</a></dd>
                            </dl>
                            <dl v-if="report.caseworkerActive && report.caseworkerEmail" class="contact-card__email">
                                <dt class="contact-card__label">E-mail</dt>
                                <dd class="contact-card__value"><a :href="`mailto:${report.caseworkerEmail}`">{{ report.caseworkerEmail }}</a></dd>
                            </dl>
                        </div>
                    </template>
                    <template v-if="userInfo.permissions.tasks.enabled">
                        <hr />
                        <router-link class="contact-card__link" :to="{ name: 'taskid', params: { id: requisition.task.taskId } }">
                            Gå til opgave &raquo;
                        </router-link>
                    </template>
                </section>
            </main>
            <div v-show="loading" class="loading-div">
                <h1 class="loading-header">Henter entreprenøropgave...</h1>
            </div>
        </div>

        <base-map ref="baseMap" :config="mapConfig" :highlightedObject="highlightedObject" :supplyCode="selectedSupplyType.code" :supplyTypeId="selectedSupplyType.id" />
        <modal v-if="showModal" :type="modalType" :data="modalData" @close="closeModal" />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import baseMap from '@/components/shared/map/base-map.vue'
import { NMapConfig, NMapButton } from 'ncomponents-map'
import NFiles from '@/components/shared/files/files.vue'
import { wait } from '@/mixins/shared/helpers'
import guideNav from '@/components/shared/nav/guide-nav.vue'
import bottomNav from '@/components/shared/nav/bottom-nav.vue'
import objects from '@/components/shared/list/list-objects.vue'
import modal from '@/components/shared/modal/modal.vue'
import axios from '@/store/api.js'

export default {
    name: 'requisition',
    components: {
        modal,
        'base-map': baseMap,
        'NFiles': NFiles,
        'guide-nav': guideNav,
        'bottom-nav': bottomNav,
        'objects-list': objects
    },
    data () {
        return {
            step: 0,
            report: {},
            saved: false,
            savedTimeStamp: 'null',
            statusTypeId: '',
            isInitialList: true,
            totalActivityCount: 0,
            // loading
            loading: false,
            approveStatus: 'Færdigmeld',
            requisitionStatus: null,
            hideApproveButton: false,
            fullAddress: '',
            existingWork: null,
            enquiryWkt: '',
            // modal
            showModal: false,
            modalType: null,
            modalData: null,
            hideMap: false,
            mapConfig: null,
            zoomLimit: 13.5,

            uploadStatusTimer: null,
            uploadStatus: 'initial',
            uploadErrorResponse: null,

            requisitionDocumentsMetadataVisible: true,
            sortKey: 'dateCreated',
            sortDirection: 'DESC',

            objectSummary: []
        }
    },
    computed: {
        ...mapGetters({
            applicationSettings: 'getApplicationSettings',
            userId: 'getUserId',
            userInfo: 'userInfo',
            organizationUserList: 'entrepreneursList',
            requisition: 'reportItemWhole',
            statusTypeList: 'getSysRequisitionStatusTypeList',
            requisitionVersionStamp: 'getRequisitionVersionStamp',
            requisitionDocuments: 'getRequisitionDocuments',
            enquiryDocuments: 'getRequisitionEnquiryDocuments',
            taskDocuments: 'getRequisitionTaskDocuments',
            selectedSupplyType: 'selectedSupplyType',
            getToggleMapState: 'getToggleMapState'
        }),
        iconObj () {
            return [
                { icon: 'task-check', text: 'Aktiviteter', active: true },
                { icon: 'files', text: 'Dokumentation', active: true, counter: this.documentationDocumentsSorted && this.documentationDocumentsSorted.length > 0 ? this.documentationDocumentsSorted.length : null },
                { icon: 'pictures', text: 'Tilføj filer', active: true, counter: this.requisitionDocuments && this.requisitionDocuments.length > 0 ? this.requisitionDocuments.length : null },
                { icon: 'info-1', text: 'Informationer', active: true }
            ]
        },
        allActivities () {
            if (this.requisition.objectList) {
                const allActivities = this.requisition.objectList.map(o => o.activityList.length)
                const allActivitiesSum = allActivities.reduce((accumulator, currentValue) => {
                    return accumulator + currentValue
                }, 0)

                return allActivitiesSum
            }
        },
        reportedActivities () {
            if (this.requisition.objectList) {
                const reportedActivities = this.requisition.objectList.map(o => o.activityList.filter(a => a.reportWorkDone !== null).length)
                const reportedActivitiesSum = reportedActivities.reduce((accumulator, currentValue) => {
                    return accumulator + currentValue
                }, 0)

                return reportedActivitiesSum
            }
        },
        checkApprove () {
            if (this.requisition.objectList) {
                const reportedActivities = this.requisition.objectList.map(o => o.activityList.filter(a => a.reportWorkDone !== null).length)
                const reportedActivitiesSum = reportedActivities.reduce((accumulator, currentValue) => {
                    return accumulator + currentValue
                }, 0)

                if (this.totalActivityCount === reportedActivitiesSum) {
                    return false
                }
                return true
            }
        },

        documentationDocumentsSorted () {
            return [...this.enquiryDocuments, ...this.taskDocuments].sort((a, b) => {
                let modifier = 1

                if (this.sortDirection === 'DESC') {
                    modifier = -1
                }

                if (a[this.sortKey] < b[this.sortKey]) {
                    return -1 * modifier
                }
                if (a[this.sortKey] > b[this.sortKey]) {
                    return 1 * modifier
                }

                return 0
            })
        },
        requisitionDocumentsSorted () {
            return [...this.requisitionDocuments].sort((a, b) => {
                let modifier = 1

                if (this.sortDirection === 'DESC') {
                    modifier = -1
                }

                if (a[this.sortKey] < b[this.sortKey]) {
                    return -1 * modifier
                }
                if (a[this.sortKey] > b[this.sortKey]) {
                    return 1 * modifier
                }

                return 0
            })
        },

        objectSummarySorted () {
            return this.objectSummary.sort((a, b) => {
                return a.activityType.localeCompare(b.activityType)
            })
        }
    },
    methods: {
        ...mapActions({
            getCurrentUserInfo: 'getCurrentUserInfo',
            getEntrepreneurs: 'getEntrepreneurs',
            getAllTypeLists: 'getAllTypeLists',
            setBusinessAreaTypes: 'setBusinessAreaTypes',
            getReport: 'getReport',
            setReportReady: 'setReportReadyForApproval',
            setRequisitionStatus: 'setRequisitionStatus',
            // other
            setEntrepreneur: 'setEntrepreneur',
            toggleMapState: 'toggleMapState',
            setSupplyType: 'setSelectedSupplyTypeFromId',
            setReportFullObjectList: 'setReportFullObjectList',
            getReportBbox: 'getReportBbox',

            // FILES
            documentAdd: 'requisitionDocumentAdd',
            documentRemove: 'requisitionDocumentRemove',
            documentOpen: 'requisitionDocumentOpen',
            documentDownload: 'requisitionDocumentDownload',
            documentDescriptionUpdate: 'requisitionDescriptionUpdate'
        }),
        checkZoomLevel () {
            if (this.mapConfig !== null) {
                return this.mapConfig.getZoom()
            }
            return 0
        },
        changedStep (result) {
            this.step = result
        },
        getRequisition () {
            if (this.$route.params.id) {
                this.getReport({ id: this.$route.params.id }).then(() => {
                    this.setReporter()

                    // Set users in the same orginazation
                    this.setEntrepreneurList()

                    // setCurrentAddress
                    if (this.requisition && this.requisition.task) {
                        let address = ''
                        let task = this.requisition.task
                        if (task.address1) {
                            address = task.address1 + ', '
                        }
                        if (task.address2) {
                            address += task.address2 + ', '
                        }
                        if (task.postalCode) {
                            address += task.postalCode + ' '
                        }
                        if (task.city) {
                            address += task.city
                        }
                        this.fullAddress = address

                        this.setSupplyType(task.businessAreaTypeId)
                    }

                    // Highlight the objects and zoom
                    if (this.requisition && this.requisition.objectList) {
                        if (this.requisition.objectList.length > 0) {
                            let foundWkt = this.requisition.objectList.some(obj => obj.geomWkt !== null)
                            if (foundWkt) {
                                setTimeout(() => {
                                    this.$refs.baseMap.fitAllObjects(this.requisition.objectList)
                                    this.$refs.baseMap.highlightObjects(this.requisition.objectList)
                                }, 2000)
                            } else { // No objects has coords
                                this.$refs.baseMap.navigateTo(this.requisition.task.locationPointWkt)
                            }
                        } else {
                            this.$refs.baseMap.navigateTo('POINT EMPTY')
                        }
                    }
                    this.totalActivityCount = this.allActivities

                    this.objectSummary = this.requisition.reportObjectSummaries
                })
            }
        },
        setReporter () {
            this.report = {
                caseworkerActive: this.requisition.task.ownerActive,
                caseworkerName: this.requisition.task.ownerName,
                caseworkerPhone: this.requisition.task.ownerPhoneNumber,
                caseworkerEmail: this.requisition.task.ownerEmail,
                clients: this.requisition.requestClientList,
                ownerUserId: this.requisition.requisition.sentToUserId,
                requisition: this.requisition.requisition,
                name: this.requisition.task.name,
                activities: this.requisition.activityIdList
            }
        },
        setEntrepreneurList () {
            this.getEntrepreneurs(this.requisition.requisition.sentToOrganizationId)
        },
        selectEntrepreneur ($event) {
            let selection = {}
            if (this.organizationUserList[$event.target.selectedIndex]) {
                selection = this.organizationUserList[$event.target.selectedIndex]
                this.report.ownerUserId = selection.userId
                new Promise((resolve) => {
                    resolve(this.setEntrepreneur(this.report))
                }).then((response) => {
                    for (let i in this.requisition.objectList) {
                        for (let x in this.requisition.objectList[i].activityList) {
                            this.requisition.objectList[i].activityList[x].activityVersionStamp = response.filter(activity => {
                                if (activity.activityId === this.requisition.objectList[i].activityList[x].id) {
                                    return activity.activityVersionStamp
                                }
                            })
                        }
                    }
                })
            }
        },
        accept () {
            this.statusTypeId = ''
            this.statusText = ''
            for (const i in this.statusTypeList) {
                if (this.statusTypeList[i].code === 4) {
                    this.statusTypeId = this.statusTypeList[i].sysRequisitionStatusTypeId
                }
            }
            this.statusText = 'Entreprenøropgave accepteret'
            this.changeRequisitionStatus(this.statusTypeId, this.statusText, 'accept')
        },
        showSummaryTable (summary) {
            return summary.some((activity) => {
                const points = (activity.numberOfPointObjectsNotDone || 0) + (activity.numberOfPointObjectsDone || 0)
                const lines = (activity.totalLengthNotDone || 0) + (activity.totalLengthDone || 0)
                const areas = (activity.totalAreaNotDone || 0) + (activity.totalAreaDone || 0)
                const others = (activity.numberOfOthersNotDone || 0) + (activity.numberOfOthersDone || 0)

                return (points + lines + areas + others) > 0
            })
        },
        openModal (type, data) {
            this.showModal = true
            this.modalType = type
            this.modalData = data
        },
        closeModal () {
            if (this.modalData.updating) {
                this.documentDescriptionUpdate(this.modalData)
            }

            this.showModal = false
        },
        changeRequisitionStatus (statusId, statusText, text) {
            let requisitionData = {
                'requisitionId': this.$route.params.id,
                'requisitionStatusId': statusId,
                'logText': statusText,
                'requisitionVersionStamp': this.requisitionVersionStamp
            }
            this.setRequisitionStatus(requisitionData).then(() => {
                this.requisitionStatus = text
            })
        },
        readyForapproval () {
            if (this.approveStatus !== 'Færdigmelder...') {
                this.approveStatus = 'Færdigmelder...'
                let dataObj = {
                    requisitionId: this.$route.params.id,
                    readyForapproval: true,
                    reportVersionStamp: this.requisition.report.reportVersionStamp
                }
                this.setReportReady(dataObj).then(response => {
                    this.approveStatus = 'Færdigmeldt'
                    this.hideApproveButton = true
                    setTimeout(() => {
                        this.$router.push({name: 'dashboard'})
                    }, 1000)
                })
            }
        },
        toggleMap () {
            this.toggleMapState(true)
        },
        setupRequisition () {
            this.loading = true
            new Promise((resolve) => {
                if (!this.userId) {
                    resolve(this.getCurrentUserInfo())
                } else {
                    resolve(true)
                }
            }).then(() => {
                return this.getAllTypeLists()
            }).then((response) => {
                return this.setBusinessAreaTypes(response.sysBusinessAreaTypeList)
            }).then(() => {
                // Get the task
                this.getRequisition()
            }).then(() => {
                this.loading = false
            })
        },
        setupMap () {
            this.mapConfig = new NMapConfig()

            const mapButtonObjectsFit = new NMapButton({
                content: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="-4 -4 32 32" width="24" height="24"><path stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none" d="M 1,7 L 1,3 S 1,1 3,1 L 7,1"/><path stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none" d="M 17,1 L 21,1 S 23,1 23,3 L 23,7"/><path stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none" d="M 23,17 L 23,21 S 23,23 21,23 L 17,23"/><path stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none" d="M 7,23 L 3,23 Q 1,23 1,21 L 1,17"/><polyline stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none" points="14,6 18,6 18,10"/><line stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none" x1="6" y1="18" x2="18" y2="6"/><polyline stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none" points="6,14 6,18 10,18"/></svg>',
                title: 'Zoom til alle objekter'
            })
            mapButtonObjectsFit.on('click', () => {
                this.$refs.baseMap.fitAllObjects(this.requisition.objectList)
            })
            this.mapConfig.addButton(mapButtonObjectsFit)
        },
        highlightedObject (objects) {
            this.toggleMapState(false)
            this.$nextTick(() => {
                objects.forEach((object) => {
                    const element = document.getElementById(`activity-${object.feature.cargo.objectId}`)
                    if (element) {
                        element.classList.add('highlighted')
                        setTimeout(() => {
                            element.classList.remove('highlighted')
                        }, 5000)
                    }
                })
                if (objects.length > 0) {
                    const element = document.getElementById(`activity-${objects[0].feature.cargo.objectId}`)
                    if (element) {
                        element.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center'
                        })
                    }
                }
            })
        },

        uploadRequisitionFiles (element) {
            const startTime = performance.now()
            let endTime = startTime

            clearTimeout(this.uploadStatusTimer)
            this.uploadStatus = 'saving'
            this.uploadErrorResponse = null

            Object.values(element.files).forEach((file) => {
                const objectData = {
                    data: new FormData(),
                    dataType: file.type,
                    file: file
                }

                objectData.data.append('content', file, file.name)

                this.documentAdd(objectData)
                    .then((result) => {
                        endTime = performance.now()

                        return result
                    })
                    .then(wait(250 - (endTime - startTime > 250 ? 250 : endTime - startTime))) // Make sure spinner is shown at least 250ms
                    .then(() => {
                        this.uploadStatus = 'success'
                        // document.getElementById(this.$children.find(child => child.$options.name === 'NFiles').$children.find(child => child.inputId).inputId).value = ''
                        this.uploadStatusTimer = setTimeout(() => {
                            this.uploadStatus = 'initial'
                        }, 3500)
                    })
                    .catch((error) => {
                        this.uploadErrorResponse = error.response && error.response.data
                            ? error.response.data
                            : error.request && error.request.responseText
                                ? error.request.responseText
                                : error
                        this.uploadStatus = 'failed'
                        this.uploadStatusTimer = setTimeout(() => {
                            this.uploadStatus = 'initial'
                        }, 5000)
                    })
            })
        },
        getIcon (file) {
            // Determine icon by mime type
            let iconName = 'unknown'

            if (file.contentType.indexOf('pdf') > -1) {
                iconName = 'pdf'
            } else if (file.contentType.indexOf('officedocument') > -1 && file.contentType.indexOf('.document') > -1) {
                iconName = 'doc'
            } else if (file.contentType.indexOf('officedocument') > -1 && file.contentType.indexOf('.presentation') > -1) {
                iconName = 'ppt'
            } else if (file.contentType.indexOf('officedocument') > -1 && file.contentType.indexOf('.sheet') > -1) {
                iconName = 'xls'
            } else if (file.contentType.indexOf('csv') > -1) {
                iconName = 'csv'
            } else if (file.contentType.indexOf('plain') > -1) {
                iconName = 'txt'
            } else if (file.contentType.indexOf('xml') > -1) {
                iconName = 'xml'
            }

            return iconName
        },

        checkDescriptionAvailability (file) {
            if (!file.description && this.hideDescription) {
                return false
            } else {
                return true
            }
        },
        commentFile (file) {
            this.openModal('document-modal-description', file)
        },

        documentListMetadataToggle () {
            this.requisitionDocumentsMetadataVisible = event.target.checked
            localStorage.setItem('requisitionDocumentsMetadataVisible', event.target.checked)
        },

        updateObjectSummary () {
            axios.get(`/Report/GetReportObjectSummaries?requisitionId=${this.requisition.report.requisitionId}`)
                .then((response) => {
                    this.objectSummary = response.data.model
                })
        }
    },
    created () {
        this.setupRequisition()
        this.setupMap()
        this.setReportFullObjectList([])
        if (localStorage.getItem('requisitionDocumentsMetadataVisible') === 'true') {
            this.requisitionDocumentsMetadataVisible = true
        }
    }
}
</script>

<style lang="scss">
@import '../scss/components/dfiles.scss';
@import '../scss/components/loader';
@import '../scss/pages/requisition';
</style>



// WEBPACK FOOTER //
// src/pages/requisition.vue